import React, { useState } from "react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Mail, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { residentApi, errMsg } from "@/lib/api";
import { useResident } from "@/lib/residentAuth";
import { Wordmark } from "@/components/Brand";
import { Field } from "@/components/form";

const DEMO = "resident.demo@nexstays.live";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function ResidentLogin() {
  const { login } = useResident();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<null | { demo: boolean }>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e = email) => {
    setBusy(true);
    try {
      const r = await residentApi.post("/resident/auth/otp/request", { email: e.trim() });
      setSent({ demo: r.data.demo });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const verify = async (c = code) => {
    setBusy(true);
    try {
      const r = await residentApi.post("/resident/auth/otp/verify", { email: email.trim(), code: c });
      await login(r.data.token);
    } catch (err) { toast.error(errMsg(err)); setCode(""); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#f7f9fc]">
      <div className="max-w-md mx-auto px-5 pt-6 pb-10">
        <Wordmark size={40} />
        <div className="mt-12 pg-rise">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#3d52a0]">For residents</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] text-[#131e2e]">Find your PG. Track your stay.</h1>
          <p className="mt-3 text-slate-500 text-sm">Students & working professionals: search PGs, request to join, see rent, receipts, menu and raise complaints.</p>
        </div>
        <div className="mt-8 rounded-3xl bg-white border border-slate-200/80 p-5 shadow-sm pg-rise">
          {!sent ? (
            <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); if (EMAIL_RE.test(email.trim())) send(); }}>
              <Field label="Email address">
                <div className="flex items-center h-14 rounded-xl border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-[#131e2e]">
                  <Mail className="ml-4 h-5 w-5 text-slate-400" />
                  <input data-testid="resident-email-input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="flex-1 h-full px-3 bg-transparent outline-none text-base" />
                </div>
              </Field>
              <Button type="submit" data-testid="resident-send-otp-btn" disabled={!EMAIL_RE.test(email.trim()) || busy} className="w-full h-14 rounded-xl text-base bg-[#131e2e] hover:bg-[#0b1320]">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send login code"}
              </Button>
              <button type="button" data-testid="resident-demo-btn" onClick={() => { setEmail(DEMO); send(DEMO); }} className="w-full h-12 rounded-xl border border-dashed border-[#a9c4e6] bg-[#eef4fb] text-[#131e2e] text-sm font-semibold flex items-center justify-center gap-2 pg-tap">
                <Sparkles className="h-4 w-4" /> Try demo resident
              </button>
            </form>
          ) : (
            <div className="space-y-5">
              <p className="text-sm text-slate-600">Code sent to <b className="break-all">{email.trim()}</b>{" "}
                <button data-testid="resident-change-email-btn" onClick={() => { setSent(null); setCode(""); }} className="text-[#131e2e] font-semibold underline">Change</button></p>
              <InputOTP data-testid="resident-otp-input" maxLength={6} value={code} autoFocus onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }}>
                <InputOTPGroup className="gap-2">
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-14 w-12 rounded-xl border text-xl font-bold bg-white" />)}
                </InputOTPGroup>
              </InputOTP>
              <p data-testid="resident-otp-hint" className="text-xs text-slate-500">{sent.demo ? "Demo resident: use code 123456" : "Check your inbox (and spam). The code expires in 5 minutes."}</p>
              <Button data-testid="resident-verify-btn" disabled={code.length !== 6 || busy} onClick={() => verify()} className="w-full h-14 rounded-xl text-base bg-[#131e2e] hover:bg-[#0b1320]">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify & continue"}
              </Button>
            </div>
          )}
        </div>
        <p className="mt-6 text-xs text-slate-500">PG owner? <Link data-testid="resident-owner-login-link" to="/login" className="font-semibold text-[#131e2e] underline">Owner login</Link></p>
      </div>
    </div>
  );
}
